import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin, of, timer } from 'rxjs';
import { catchError, takeUntil, switchMap } from 'rxjs/operators';
import { AgentConversationApiService } from '../../services/agent-conversation-api.service';
import {
  AgentConversationDetail,
  AgentConversationListItem,
  KnowledgeSuggestion,
  conversationStatusLabel
} from '../../models/agent-conversation.model';
import { AgentMessage } from '../../models/agent-message.model';
import { AgentConversationTranscriptComponent } from '../agent-conversation-transcript/agent-conversation-transcript.component';
import { AgentConversationInputComponent } from '../agent-conversation-input/agent-conversation-input.component';
import { AgentAttachmentUploadResponse } from '../../models/agent-action.model';
import { ContactTicketHistoryItem } from '../../../support-tickets/models/support-ticket.model';
import { SupportTicketApiService } from '../../../support-tickets/services/support-ticket-api.service';
import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import { ChatflowPermissionService } from '../../../chatflows/services/chatflow-permission.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { AgentLookupApiService } from '../../../chatflows/services/agent-lookup-api.service';
import { ChatTeamLookupApiService } from '../../../chatflows/services/chat-team-lookup-api.service';
import { AgentLookupResponse, TeamLookupResponse, agentDisplayLabel, teamDisplayLabel } from '../../../chatflows/models/agent-lookup.model';
import { TicketsConfigApiService } from '../../../tickets/services/tickets-config-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { TicketCategory, TicketPipeline, TicketPriority, TicketStatus } from '../../../tickets/models/tickets-config.model';

/** Formulario del modal "Crear ticket" (inc68). */
interface TicketFormModel {
  subject: string;
  description: string;
  pipelineId: number | null;
  statusId: number | null;
  priorityId: number | null;
  categoryId: number | null;
}

@Component({
  selector: 'app-agent-conversation-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AgentConversationTranscriptComponent, AgentConversationInputComponent, ModalComponent],
  template: `
    <!-- Estado vacío -->
    <div *ngIf="!conversationId" class="flex-1 flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
      <div class="text-center text-gray-400 dark:text-gray-600 px-8">
        <svg class="w-20 h-20 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
          </path>
        </svg>
        <p class="text-base font-medium">Selecciona una conversación</p>
        <p class="text-sm mt-1 opacity-60">Elige un chat de la bandeja para ver los detalles y responder.</p>
      </div>
    </div>

    <!-- Conversación activa -->
    <div *ngIf="conversationId" class="flex flex-col h-full min-h-0">

      <!-- Header -->
      <div class="h-16 shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 flex items-center justify-between shadow-sm">
        <div class="flex items-center gap-3 min-w-0">
          <div class="w-9 h-9 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow">
            {{ (detail?.visitorName || 'U').charAt(0).toUpperCase() }}
          </div>
          <div class="min-w-0">
            <!-- Sin título, manda el nombre del visitante; el número va siempre, que es lo único
                 que distingue a dos "Visitante Anónimo". -->
            <div *ngIf="!editandoTitulo" class="flex items-center gap-1 min-w-0">
              <!-- min-w-0: sin esto, en ventana estrecha el lapiz (shrink-0) se queda con el sitio
                   y el titulo se encoge hasta desaparecer en vez de recortarse con puntos. -->
              <h3 class="font-semibold text-sm text-gray-800 dark:text-gray-100 leading-tight truncate min-w-0">
                {{ detail?.agentTitle || detail?.visitorName || 'Visitante Anónimo' }}
                <span class="text-[10px] bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded ml-1 text-gray-500 font-normal">#{{ detail?.conversationId }}</span>
              </h3>
              <button type="button" (click)="empezarEdicionTitulo()"
                      [title]="detail?.agentTitle ? 'Cambiar el título de la conversación' : 'Poner un título a la conversación'"
                      class="shrink-0 p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"></path>
                </svg>
              </button>
            </div>

            <div *ngIf="editandoTitulo" class="flex items-center gap-1">
              <input #tituloInput type="text" [(ngModel)]="tituloBorrador" maxlength="120"
                     (keydown.enter)="guardarTitulo()" (keydown.escape)="cancelarEdicionTitulo()"
                     placeholder="Título de la conversación"
                     class="w-56 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              <button type="button" (click)="guardarTitulo()" [disabled]="guardandoTitulo"
                      class="px-2 py-1 text-[11px] font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50">
                {{ guardandoTitulo ? '…' : 'Guardar' }}
              </button>
              <button type="button" (click)="cancelarEdicionTitulo()"
                      class="px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition">
                Cancelar
              </button>
            </div>
            <div class="text-[10px] text-gray-400 flex items-center gap-2 mt-0.5">
              <span class="flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full"
                      [ngClass]="{
                        'bg-yellow-500': detail?.status === 'WAITING_AGENT',
                        'bg-green-500': detail?.status === 'LIVE_CHAT',
                        'bg-gray-400': detail?.status === 'CLOSED'
                      }"></span>
                <!-- El backend manda el estado en inglés y en mayúsculas (WAITING_AGENT):
                     es un valor técnico, no una etiqueta de interfaz. -->
                {{ detail?.status ? estadoLegible(detail?.status) : 'Cargando...' }}
              </span>
              <span *ngIf="detail?.agentTitle">&bull; {{ detail?.visitorName || 'Visitante Anónimo' }}</span>
              <!-- inc47: el flujo de origen se identifica por su nombre -->
              <span *ngIf="detail?.chatflowName">&bull; {{ detail?.chatflowName }}</span>
              <span *ngIf="detail?.assignedAgentName">&bull; {{ detail?.assignedAgentName }}</span>
            </div>
          </div>
        </div>

        <!-- Acciones (conversación activa) -->
        <div class="flex items-center gap-1.5 shrink-0 ml-2" *ngIf="detail && detail.status !== 'CLOSED'">
          <button (click)="toggleCrmPanel()"
            title="Panel CRM"
            class="px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border transition"
            [ngClass]="showCrmPanel
              ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
              : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'">
            CRM
          </button>
          <button (click)="assignToMe()" [disabled]="assigning"
            class="px-2.5 py-1.5 text-[11px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-800 transition disabled:opacity-50">
            {{ assigning ? '…' : 'Tomar' }}
          </button>
          <button (click)="abrirTransferir()"
            class="px-2.5 py-1.5 text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 transition">
            Transferir
          </button>
          @if (createdTicketId) {
            <button (click)="verTicket()" [title]="'Abrir el ticket ' + etiquetaTicket"
              class="px-2.5 py-1.5 text-[11px] font-semibold bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 rounded-lg border border-purple-200 dark:border-purple-700 hover:bg-purple-100 transition">
              🎫 {{ etiquetaTicket }}
            </button>
          } @else {
            <button (click)="abrirTicket()"
              class="px-2.5 py-1.5 text-[11px] font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg border border-purple-200 dark:border-purple-800 transition">
              Ticket
            </button>
          }
          @if (permissions.canExportTranscripts()) {
            <button (click)="exportarTranscript('txt')" title="Exportar TXT"
              class="px-2.5 py-1.5 text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 transition">
              ↓ TXT
            </button>
          }
          @if (permissions.canDeleteTranscripts() || permissions.canManagePrivacy()) {
            <button (click)="solicitarAccionDestructiva('ANONYMIZE')" title="Anonimizar"
              class="px-2.5 py-1.5 text-[11px] font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 rounded-lg border border-amber-200 dark:border-amber-700 transition">
              Anon.
            </button>
          }
          <button (click)="abrirCerrar()"
            class="px-2.5 py-1.5 text-[11px] font-medium bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800 transition">
            Finalizar
          </button>
        </div>

        <!-- Acciones (conversación cerrada) -->
        <div *ngIf="detail?.status === 'CLOSED'" class="flex items-center gap-1.5 shrink-0">
          <button (click)="toggleCrmPanel()"
            title="Panel CRM"
            class="px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border transition"
            [ngClass]="showCrmPanel
              ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
              : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'">
            CRM
          </button>
          @if (createdTicketId) {
            <button (click)="verTicket()"
              class="px-2.5 py-1.5 text-[11px] font-semibold bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 rounded-lg border border-purple-200 dark:border-purple-700 hover:bg-purple-100 transition">
              🎫 {{ etiquetaTicket }}
            </button>
          } @else {
            <button (click)="abrirTicket()"
              class="px-2.5 py-1.5 text-[11px] font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg border border-purple-200 dark:border-purple-800 transition">
              Crear Ticket
            </button>
          }
          <span class="text-[11px] bg-gray-200 dark:bg-gray-700 text-gray-500 px-3 py-1.5 rounded-lg">Cerrada</span>
        </div>
      </div>

      <!-- Motivo de cierre: la trazabilidad que el modal de finalizar promete (inc70) -->
      <div *ngIf="detail?.status === 'CLOSED' && detail?.closeReason"
           class="shrink-0 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800 px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
        <span class="font-semibold">Motivo de cierre:</span> {{ detail?.closeReason }}
      </div>

      <!-- Error banner -->
      <div *ngIf="actionError"
           class="shrink-0 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-2 text-sm flex justify-between items-center">
        <span>{{ actionError }}</span>
        <button (click)="actionError = ''" class="ml-4 font-bold text-lg leading-none">&times;</button>
      </div>

      <!-- Badges de transcript eliminado/anonimizado -->
      @if (transcriptDeleted || transcriptAnonymized) {
        <div class="shrink-0 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01"/></svg>
          {{ transcriptDeleted ? 'Transcript eliminado. Los datos personales han sido removidos.' : 'Conversación anonimizada. Los datos personales han sido reemplazados.' }}
        </div>
      }

      <!-- Layout con panel CRM opcional -->
      <div class="flex flex-1 min-h-0 overflow-hidden">

        <!-- ── RF-05: Panel Contexto CRM ── -->
        <div *ngIf="showCrmPanel"
             class="w-60 shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto flex flex-col">

          <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Contexto CRM</span>
          </div>

          <!-- Caso A: contactId presente → Contacto asociado -->
          <ng-container *ngIf="detail?.contactId; else noContact">
            <div class="p-4 space-y-3">
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                <span class="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Contacto asociado</span>
              </div>

              <ng-container *ngIf="detail?.contactSummary as cs">
                <div *ngIf="cs.name" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Nombre</span>
                  <span class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ cs.name }}</span>
                </div>
                <div *ngIf="cs.email" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Email</span>
                  <span class="text-xs text-gray-700 dark:text-gray-300 break-all">{{ cs.email }}</span>
                </div>
                <div *ngIf="cs.phone" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Teléfono</span>
                  <span class="text-xs text-gray-700 dark:text-gray-300">{{ cs.phone }}</span>
                </div>
                <div *ngIf="cs.position" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Cargo</span>
                  <span class="text-xs text-gray-700 dark:text-gray-300">{{ cs.position }}</span>
                </div>
                <div *ngIf="cs.ownerName" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Responsable asignado</span>
                  <span class="text-xs text-gray-700 dark:text-gray-300">{{ cs.ownerName }}</span>
                </div>
                <div *ngIf="cs.companyName" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Empresa</span>
                  <span class="text-xs text-gray-700 dark:text-gray-300">{{ cs.companyName }}</span>
                </div>
                <div *ngIf="cs.lifecycleStage" class="flex flex-col gap-0.5">
                  <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Etapa</span>
                  <span class="text-[10px] bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full w-fit font-medium">
                    {{ cs.lifecycleStage }}
                  </span>
                </div>
              </ng-container>

              <!-- IDs -->
              <div class="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-1">
                <div class="flex justify-between text-[10px]">
                  <span class="text-gray-400">ID Contacto</span>
                  <span class="font-mono text-gray-600 dark:text-gray-400">#{{ detail?.contactId || detail?.contactSummary?.id }}</span>
                </div>
                <div *ngIf="detail?.companyId" class="flex justify-between text-[10px]">
                  <span class="text-gray-400">ID Empresa</span>
                  <span class="font-mono text-gray-600 dark:text-gray-400">#{{ detail?.companyId }}</span>
                </div>
              </div>

              <!-- Company summary -->
              <ng-container *ngIf="detail?.companySummary as co">
                <div class="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
                  <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Empresa</span>
                  <div *ngIf="co.name" class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ co.name }}</div>
                  <div *ngIf="co.country" class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 uppercase tracking-wider">País</span>
                    <span class="text-xs text-gray-700 dark:text-gray-300">{{ co.country }}</span>
                  </div>
                  <div *ngIf="co.industry" class="flex flex-col gap-0.5">
                    <span class="text-[10px] text-gray-400 uppercase tracking-wider">Industria</span>
                    <span class="text-xs text-gray-700 dark:text-gray-300">{{ co.industry }}</span>
                  </div>
                </div>
              </ng-container>

              <!-- Ver Contacto button -->
              <div class="pt-2">
                <button (click)="verContacto()" class="w-full px-3 py-1.5 text-[11px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-700 transition">
                  Ver ficha del contacto
                </button>
              </div>

              <!-- History -->
              <div class="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-4">

                <!-- Tickets -->
                <div>
                  <h4 class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">Tickets Anteriores</h4>
                  <div *ngIf="loadingHistory" class="text-xs text-gray-400">Cargando...</div>
                  <div *ngIf="!loadingHistory && pastTickets.length === 0" class="text-[10px] text-gray-500">No hay tickets.</div>
                  <div class="space-y-2">
                    <!-- Nombres, no ids: el endpoint de historial ya devuelve statusName y
                         priorityName resueltos. -->
                    <div *ngFor="let t of pastTickets" (click)="verTicketDelHistorial(t.id)"
                         class="bg-gray-50 dark:bg-gray-800/50 p-2 rounded border border-gray-100 dark:border-gray-800 cursor-pointer hover:border-brand-300 transition">
                      <div class="flex justify-between items-start mb-1">
                        <span class="text-[10px] font-mono text-blue-600 dark:text-blue-400">{{ t.ticketNumber }}</span>
                        <span *ngIf="t.statusName" class="text-[9px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{{ t.statusName }}</span>
                      </div>
                      <p class="text-xs text-gray-800 dark:text-gray-200 font-medium truncate">{{ t.subject }}</p>
                      <div class="flex justify-between items-center mt-1 text-[10px] text-gray-500">
                        <span>{{ t.priorityName }}</span>
                        <span>{{ t.datetimeCreated | date:'shortDate' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Conversaciones -->
                <div>
                  <h4 class="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">Conversaciones Anteriores</h4>
                  <div *ngIf="loadingHistory" class="text-xs text-gray-400">Cargando...</div>
                  <div *ngIf="!loadingHistory && pastConversations.length === 0" class="text-[10px] text-gray-500">No hay conversaciones.</div>
                  <div class="space-y-2">
                    <div *ngFor="let c of pastConversations" class="bg-gray-50 dark:bg-gray-800/50 p-2 rounded border border-gray-100 dark:border-gray-800">
                      <!-- Cabecera de la conversacion -->
                      <div class="flex items-center justify-between mb-1">
                        <span class="text-[10px] font-mono text-gray-500">#{{ c.conversationId }}</span>
                        <span class="text-[10px] font-bold px-2 py-0.5 rounded-full"
                              [ngClass]="{
                                'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400': c.status === 'LIVE_CHAT',
                                'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400': c.status === 'CLOSED',
                                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400': c.status === 'WAITING_AGENT'
                              }">
                          {{ estadoLegible(c.status) }}
                        </span>
                      </div>
                      <!-- inc47: el flujo de origen por su nombre, nunca por su id -->
                      <p class="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                         {{ c.chatflowName || (c.chatflowId ? 'Chatflow #' + c.chatflowId : 'Sin mensajes') }}
                      </p>
                      <div class="flex items-center justify-between mt-2 text-[10px] text-gray-400">
                        <!-- El nombre del agente lo resuelve el backend en el listado; el id
                             interno no le dice nada a quien atiende. -->
                        <span class="truncate pr-2">{{ c.assignedAgentName || 'Sin agente' }}</span>
                        <span class="shrink-0">{{ c.lastMessageAt | date:'shortDate' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- RF-08: Artículos sugeridos al visitante -->
                <div *ngIf="knowledgeSuggestions.length > 0">
                  <h4 class="text-[11px] font-semibold uppercase tracking-widest text-blue-400 dark:text-blue-500 mb-2">📄 Artículos sugeridos</h4>
                  <div class="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/40 rounded-lg px-3 py-2 mb-2 text-[10px] text-blue-600 dark:text-blue-400">
                    El cliente ya recibió artículos sugeridos durante el bot.
                  </div>
                  <div class="space-y-2">
                    <div *ngFor="let art of knowledgeSuggestions"
                         class="bg-white dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-700">
                      <p class="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{{ art.title }}</p>
                      <span *ngIf="art.category"
                            class="text-[9px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-medium">
                        {{ art.category }}
                      </span>
                      <p *ngIf="art.summary" class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{{ art.summary }}</p>
                      <a [href]="'/knowledge/' + art.slug" target="_blank"
                         class="mt-1.5 flex items-center gap-1 text-[10px] text-blue-500 hover:underline">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                        Ver artículo
                      </a>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </ng-container>

          <!-- Casos B & C -->
          <ng-template #noContact>
            <!-- Caso B: email disponible sin contactId -->
            <div *ngIf="detail?.visitorEmail; else anonymous" class="p-4 space-y-3">
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-yellow-500 shrink-0"></span>
                <span class="text-xs font-semibold text-yellow-700 dark:text-yellow-400">Visitante identificado</span>
              </div>
              <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
                Email capturado, no asociado al CRM.
              </p>
              <div class="flex flex-col gap-0.5">
                <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Email</span>
                <span class="text-xs text-gray-700 dark:text-gray-300 break-all">{{ detail?.visitorEmail }}</span>
              </div>
              <div *ngIf="detail?.visitorPhone" class="flex flex-col gap-0.5">
                <span class="text-[10px] font-medium uppercase tracking-wider text-gray-400">Teléfono</span>
                <span class="text-xs text-gray-700 dark:text-gray-300">{{ detail?.visitorPhone }}</span>
              </div>
            </div>

            <!-- Caso C: completamente anónimo -->
            <ng-template #anonymous>
              <div class="p-4 space-y-3">
                <div class="flex items-center gap-2">
                  <span class="w-2 h-2 rounded-full bg-gray-400 shrink-0"></span>
                  <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">Visitante anónimo</span>
                </div>
                <p class="text-[11px] text-gray-400 leading-snug">Sin datos de identificación capturados.</p>
              </div>
            </ng-template>
          </ng-template>
        </div>

        <!-- Chat column -->
        <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
          <app-agent-conversation-transcript
            class="flex-1 min-h-0 overflow-hidden flex flex-col bg-[#eae6df] dark:bg-[#0d1117]"
            [messages]="transcript">
          </app-agent-conversation-transcript>

          <app-agent-conversation-input
            class="shrink-0"
            [isDisabled]="!puedeResponder"
            [disabledMessage]="motivoSinResponder"
            [showTakeAction]="puedeTomar"
            [taking]="assigning"
            (takeRequested)="assignToMe()"
            [loading]="sending"
            [uploading]="uploadingAttachment"
            [attachment]="adjuntoListo"
            (fileSelected)="subirAdjunto($event)"
            (attachmentRemoved)="quitarAdjunto()"
            (onSend)="enviarMensaje($event)">
          </app-agent-conversation-input>
        </div>
      </div>
    </div>

    <!-- Modal confirmación acción destructiva.
         z-modal (100500) y no un z-[9999] crudo: el header pegado del CRM vive en
         z-header (99999), así que con el valor anterior la cabecera se quedaba ENCIMA
         del overlay y parecía seguir operativa (INC_CRM_019 (8) inc69). -->
    @if (showDestructiveConfirm) {
      <div class="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-md p-6 space-y-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-error-100 dark:bg-error-500/20 flex items-center justify-center shrink-0">
              <svg class="w-5 h-5 text-error-600 dark:text-error-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
            </div>
            <div>
              <h3 class="text-sm font-bold text-gray-800 dark:text-white">
                {{ showDestructiveConfirm === 'DELETE' ? 'Eliminar transcript' : 'Anonimizar conversación' }}
              </h3>
              <p class="text-xs text-gray-400 mt-0.5">Esta acción no se puede deshacer</p>
            </div>
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {{ showDestructiveConfirm === 'DELETE'
              ? 'Se eliminarán permanentemente todos los mensajes de esta conversación. Los datos del contacto en el CRM no se verán afectados.'
              : 'Los datos personales del visitante (nombre, email, teléfono) serán reemplazados por texto genérico en el transcript. Esta acción quedará registrada en auditoría.' }}
          </p>
          <div class="rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-3 py-2">
            <p class="text-[11px] text-amber-700 dark:text-amber-400">⚠ Esta acción quedará registrada en el log de auditoría.</p>
          </div>
          <div class="flex justify-end gap-2 pt-2">
            <button (click)="cancelarDestructiva()" class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition">Cancelar</button>
            <button (click)="confirmarDestructiva()" class="px-4 py-2 bg-error-600 hover:bg-error-700 text-white rounded-lg text-sm font-medium transition">
              {{ showDestructiveConfirm === 'DELETE' ? 'Sí, eliminar' : 'Sí, anonimizar' }}
            </button>
          </div>
        </div>
      </div>
    }

    <!-- ── Modal de Transferencia (inc67) ──
         [isOpen] es la entrada real de app-modal. Antes se envolvía en *ngIf con
         [title]/(closeModal), entradas que ese componente no tiene: isOpen se quedaba en
         false y pulsar "Transferir" no pintaba absolutamente nada. -->
    <app-modal [isOpen]="showTransferModal" (close)="cerrarTransferModal()" className="max-w-md mx-4">
      <div class="p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Transferir conversación</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          La conversación pasa a la bandeja del destino con todo el historial. Queda registrado
          quién la transfirió.
        </p>

        <!-- Opciones de destino -->
        <div class="flex gap-4">
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="radio" name="transferType" value="AGENT" [(ngModel)]="transferType" class="text-blue-600">
            A un Agente
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="radio" name="transferType" value="TEAM" [(ngModel)]="transferType" class="text-blue-600">
            A un Equipo
          </label>
        </div>

        <div *ngIf="loadingTransferData" class="text-sm text-gray-500 text-center py-4">
          <svg class="animate-spin h-5 w-5 text-blue-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Cargando opciones...
        </div>

        <div *ngIf="!loadingTransferData">
          <!-- Selector Agente -->
          <div *ngIf="transferType === 'AGENT'" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Seleccionar Agente</label>
            <select [(ngModel)]="selectedAgentId" class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option [ngValue]="null">-- Seleccione un agente --</option>
              <option *ngFor="let a of transferAgents" [ngValue]="a.id">{{ agentLabel(a) }}</option>
            </select>
            <p *ngIf="transferAgents.length === 0" class="text-xs text-gray-400 mt-1">No hay agentes activos disponibles.</p>
          </div>

          <!-- Selector Equipo -->
          <div *ngIf="transferType === 'TEAM'" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Seleccionar Equipo</label>
            <select [(ngModel)]="selectedTeamId" class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option [ngValue]="null">-- Seleccione un equipo --</option>
              <option *ngFor="let t of transferTeams" [ngValue]="t.id">{{ teamLabel(t) }}</option>
            </select>
            <p *ngIf="transferTeams.length === 0" class="text-xs text-gray-400 mt-1">No hay equipos de chat configurados.</p>
          </div>
        </div>

        <!-- Nota -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Nota interna (opcional)</label>
          <textarea [(ngModel)]="transferNote" rows="3" placeholder="Ej: Cliente necesita soporte avanzado..." class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"></textarea>
        </div>

        <!-- Acciones -->
        <div class="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-gray-800">
          <button (click)="cerrarTransferModal()" class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition">Cancelar</button>
          <button (click)="ejecutarTransferencia()"
                  [disabled]="transfiriendo || (transferType === 'AGENT' && !selectedAgentId) || (transferType === 'TEAM' && !selectedTeamId)"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed">
            {{ transfiriendo ? 'Transfiriendo…' : 'Confirmar transferencia' }}
          </button>
        </div>
      </div>
    </app-modal>

    <!-- ── Modal Crear Ticket (inc68) ──
         Antes el botón llamaba directamente al endpoint con un cuerpo vacío; como el asunto
         es obligatorio en el backend, la respuesta era siempre 400 y el agente solo veía
         "No se pudo crear el ticket". -->
    <app-modal [isOpen]="showTicketModal" (close)="cerrarTicketModal()" className="max-w-lg mx-4">
      <div class="p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Crear ticket desde la conversación</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          El ticket queda vinculado a esta conversación y aparece en el módulo Tickets.
          El contacto y la empresa se toman del contexto CRM de la conversación.
        </p>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Asunto <span class="text-error-500">*</span></label>
          <input type="text" [(ngModel)]="ticketForm.subject" maxlength="200"
                 placeholder="Resume el motivo del ticket"
                 class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
          <textarea [(ngModel)]="ticketForm.description" rows="5"
                    class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"></textarea>
          <p class="text-[11px] text-gray-400">Se precarga con la transcripción de la conversación. Puedes editarla.</p>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Pipeline</label>
            <select [(ngModel)]="ticketForm.pipelineId" class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option [ngValue]="null">Por defecto</option>
              <option *ngFor="let p of ticketPipelines" [ngValue]="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Estado</label>
            <select [(ngModel)]="ticketForm.statusId" class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option [ngValue]="null">Por defecto</option>
              <option *ngFor="let s of ticketStatuses" [ngValue]="s.id">{{ s.label || s.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Prioridad</label>
            <select [(ngModel)]="ticketForm.priorityId" class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option [ngValue]="null">Por defecto</option>
              <option *ngFor="let p of ticketPriorities" [ngValue]="p.id">{{ p.label || p.name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Categoría</label>
            <select [(ngModel)]="ticketForm.categoryId" class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option [ngValue]="null">Sin categoría</option>
              <option *ngFor="let c of ticketCategories" [ngValue]="c.id">{{ c.name }}</option>
            </select>
          </div>
        </div>

        <div class="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-gray-800">
          <button (click)="cerrarTicketModal()" class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition">Cancelar</button>
          <button (click)="crearTicket()" [disabled]="creatingTicket || !ticketForm.subject.trim()"
                  class="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed">
            {{ creatingTicket ? 'Creando…' : 'Crear ticket' }}
          </button>
        </div>
      </div>
    </app-modal>

    <!-- ── Modal Finalizar conversación (inc70) ──
         Sustituye al prompt() nativo del navegador, que enseñaba el dominio del CRM
         ("des.crm-front… dice") y no decía dónde acababa el motivo escrito. -->
    <app-modal [isOpen]="showCloseModal" (close)="cerrarCerrarModal()" className="max-w-md mx-4">
      <div class="p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Finalizar conversación</h2>
        <p class="text-sm text-gray-600 dark:text-gray-300">
          ¿Seguro que deseas finalizar esta conversación? Una vez finalizada, el visitante ya no
          podrá continuar enviando mensajes en esta sesión.
        </p>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Motivo de cierre (opcional)</label>
          <textarea [(ngModel)]="closeReason" rows="3" maxlength="500"
                    placeholder="Escribe el motivo por el que se finaliza la conversación..."
                    class="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"></textarea>
        </div>

        <div class="rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 px-3 py-2">
          <p class="text-[11px] text-blue-700 dark:text-blue-300">
            El motivo queda registrado en el historial de la conversación, junto con el agente que
            la cerró y la fecha. Podrás consultarlo en la cabecera de esta misma conversación y en
            la exportación de la transcripción.
          </p>
        </div>

        <div class="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-gray-800">
          <button (click)="cerrarCerrarModal()" class="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition">Cancelar</button>
          <button (click)="confirmarCierre()" [disabled]="cerrando"
                  class="px-4 py-2 bg-error-600 hover:bg-error-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50">
            {{ cerrando ? 'Finalizando…' : 'Finalizar conversación' }}
          </button>
        </div>
      </div>
    </app-modal>
  `,
  // Mismo motivo que en la bandeja: el anfitrión es `display: block` y, sin poder encogerse,
  // arrastraba el alto del contenido y rompía el scroll interno de la conversación (inc66).
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
      overflow: hidden;
    }
  `]
})
export class AgentConversationDetailComponent implements OnChanges, OnDestroy {
  @Input() conversationId: number | null = null;

  detail: AgentConversationDetail | null = null;
  transcript: AgentMessage[] = [];
  sending = false;
  assigning = false;
  actionError = '';
  showCrmPanel = false;
  hasAutoOpenedPanel = false;

  pastTickets: ContactTicketHistoryItem[] = [];
  pastConversations: AgentConversationListItem[] = [];
  loadingHistory = false;

  // RF-08: Knowledge suggestions
  knowledgeSuggestions: KnowledgeSuggestion[] = [];

  // Transfer Modal
  showTransferModal = false;
  transferType: 'AGENT' | 'TEAM' = 'AGENT';
  selectedAgentId: number | null = null;
  selectedTeamId: number | null = null;
  transferNote = '';
  transferAgents: AgentLookupResponse[] = [];
  transferTeams: TeamLookupResponse[] = [];
  loadingTransferData = false;
  transfiriendo = false;
  readonly agentLabel = agentDisplayLabel;
  readonly teamLabel = teamDisplayLabel;

  // RF-11 / inc68: Ticket creado desde bandeja
  showTicketModal = false;
  createdTicketNumber: string | null = null;
  createdTicketId: number | null = null;
  creatingTicket = false;
  ticketForm: TicketFormModel = this.emptyTicketForm();
  ticketPipelines: TicketPipeline[] = [];
  ticketStatuses: TicketStatus[] = [];
  ticketPriorities: TicketPriority[] = [];
  ticketCategories: TicketCategory[] = [];
  private ticketCatalogsLoaded = false;

  // inc70: cierre con modal propio
  showCloseModal = false;
  closeReason = '';
  cerrando = false;

  // inc71: adjuntos
  uploadingAttachment = false;

  // RF-14/15: Transcript acciones
  transcriptDeleted = false;
  transcriptAnonymized = false;
  showDestructiveConfirm: 'DELETE' | 'ANONYMIZE' | null = null;

  private destroy$ = new Subject<void>();
  private readonly POLLING_INTERVAL = 5000;

  constructor(
    private api: AgentConversationApiService,
    private router: Router,
    private ticketApi: SupportTicketApiService,
    readonly permissions: ChatflowPermissionService,
    private toast: ToastService,
    private agentLookup: AgentLookupApiService,
    private teamLookup: ChatTeamLookupApiService,
    private ticketsConfigApi: TicketsConfigApiService,
    private auth: AuthService
  ) {}

  // ── Título de la conversación ──────────────────────────────────────────────

  editandoTitulo = false;
  tituloBorrador = '';
  guardandoTitulo = false;

  @ViewChild('tituloInput') tituloInput?: ElementRef<HTMLInputElement>;

  empezarEdicionTitulo() {
    this.tituloBorrador = this.detail?.agentTitle ?? '';
    this.editandoTitulo = true;
    // El campo nace con *ngIf, asi que hay que enfocarlo cuando ya existe: `autofocus` solo
    // actua la primera vez que el navegador inserta el elemento, y al reabrir el editor el
    // agente se quedaba escribiendo en el vacio. `select()` ademas deja el titulo actual
    // marcado, que es lo comodo para renombrar de una pasada.
    setTimeout(() => this.tituloInput?.nativeElement.select());
  }

  cancelarEdicionTitulo() {
    this.editandoTitulo = false;
    this.tituloBorrador = '';
  }

  /**
   * Guarda el título. Vacío lo borra, que es como se deshace: obligar a escribir algo dejaría
   * la conversación con un nombre puesto por error para siempre.
   *
   * <p>La bandeja de la izquierda se refresca sola con su polling de 15 s.</p>
   */
  guardarTitulo() {
    if (!this.conversationId || this.guardandoTitulo) return;
    const titulo = this.tituloBorrador.trim();
    this.guardandoTitulo = true;
    this.api.updateTitle(this.conversationId, titulo || null).subscribe({
      next: (res) => {
        this.detail = res;
        this.guardandoTitulo = false;
        this.editandoTitulo = false;
        this.toast.success({
          title: titulo ? 'Título guardado' : 'Título quitado',
          message: titulo
            ? 'La conversación se identifica ahora como «' + titulo + '».'
            : 'La conversación vuelve a identificarse por el visitante y su número.'
        });
      },
      error: () => {
        this.guardandoTitulo = false;
        this.toast.error({
          title: 'No se pudo guardar el título',
          message: 'Inténtalo nuevamente.'
        });
      }
    });
  }

  // ── Quién puede escribirle al visitante ────────────────────────────────────

  /**
   * Responder exige tener la conversación tomada, igual que el backend
   * (`AgentConversationService.canReplyConversation`).
   *
   * <p>Antes el redactor solo miraba si estaba cerrada, así que desde el filtro «Todos» un
   * supervisor escribía en la conversación de otro agente sin tomarla: el visitante recibía el
   * mensaje dentro del mismo chat y la bandeja seguía enseñándola asignada al otro.</p>
   */
  get puedeResponder(): boolean {
    return !!this.detail
      && this.detail.status === 'LIVE_CHAT'
      && this.detail.assignedAgentId != null
      && this.detail.assignedAgentId === this.auth.getCurrentUser()?.id;
  }

  /** "Tomar" solo tiene sentido mientras la conversación siga abierta. */
  get puedeTomar(): boolean {
    return !!this.detail && this.detail.status !== 'CLOSED' && !this.puedeResponder;
  }

  get motivoSinResponder(): string {
    if (!this.detail) {
      return 'Selecciona una conversación para responder.';
    }
    if (this.detail.status === 'CLOSED') {
      return 'Esta conversación está cerrada. No puedes enviar más mensajes.';
    }
    if (this.detail.assignedAgentId == null) {
      return 'Nadie está atendiendo esta conversación todavía.';
    }
    const agente = this.detail.assignedAgentName?.trim();
    return agente
      ? `La está atendiendo ${agente}. Tómala para poder responder.`
      : 'La atiende otro agente. Tómala para poder responder.';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['conversationId'] && this.conversationId) {
      this.resetState();
      this.iniciarPolling();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetState() {
    this.destroy$.next();
    this.detail = null;
    this.transcript = [];
    this.actionError = '';
    this.pastTickets = [];
    this.pastConversations = [];
    this.loadingHistory = false;
    this.hasAutoOpenedPanel = false;
    this.createdTicketNumber = null;
    this.createdTicketId = null;
    this.transcriptDeleted = false;
    this.transcriptAnonymized = false;
  }

  private iniciarPolling() {
    timer(0, this.POLLING_INTERVAL)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.api.getConversationDetail(this.conversationId!))
      )
      .subscribe({
        next: (res) => {
          // Si el backend no envía 'conversation' anidado, res es directamente el detalle
          this.detail = res.conversation || (res as any);
          this.transcript = res.transcript || [];
          // El ticket vinculado viaja en el detalle, así que la marca sobrevive a recargar
          // la bandeja o a que lo cree otro agente (inc68). Se enseña su NÚMERO público
          // (TKT-1-100), que es como el agente lo busca en el módulo de Tickets.
          if (this.detail?.linkedTicketId) {
            this.createdTicketId = this.detail.linkedTicketId;
            this.createdTicketNumber = this.detail.linkedTicketNumber || this.createdTicketNumber;
          }

          const contactId = this.detail?.contactId ?? this.detail?.contactSummary?.id ?? null;

          if (contactId && !this.hasAutoOpenedPanel) {
            this.showCrmPanel = true;
            this.hasAutoOpenedPanel = true;
            this.loadHistory(contactId);
            this.loadKnowledgeSuggestions();
          } else if (this.detail?.visitorEmail && !this.hasAutoOpenedPanel) {
            this.showCrmPanel = true;
            this.hasAutoOpenedPanel = true;
            this.loadKnowledgeSuggestions();
          }
        },
        error: (err) => console.error('Polling detail error', err)
      });
  }

  private loadKnowledgeSuggestions() {
    if (!this.conversationId) return;
    this.api.getCrmContext(this.conversationId).subscribe({
      next: (ctx: any) => {
        if (ctx.knowledgeSuggestions && Array.isArray(ctx.knowledgeSuggestions)) {
          this.knowledgeSuggestions = ctx.knowledgeSuggestions;
        }
      },
      error: () => {} // silencioso
    });
  }

  toggleCrmPanel() {
    this.showCrmPanel = !this.showCrmPanel;
  }

  // ── Mensajes ───────────────────────────────────────────────────────────────

  /**
   * Envía la respuesta del agente. El texto puede venir vacío si lo que se manda es solo un
   * adjunto: el backend acepta "texto o adjunto, pero algo" (inc71).
   */
  enviarMensaje(texto: string) {
    if (!this.conversationId) return;
    const adjunto = this.adjuntoListo;
    if (!texto.trim() && !adjunto) return;

    this.sending = true;
    this.actionError = '';
    this.api.sendAgentMessage(this.conversationId, {
      contentText: texto.trim() || undefined,
      attachmentUrl: adjunto?.url,
      attachmentName: adjunto?.fileName,
      attachmentContentType: adjunto?.contentType,
      attachmentSize: adjunto?.fileSize
    }).subscribe({
      next: (msg) => {
        this.transcript = [...this.transcript, msg];
        this.adjuntoListo = null;
        this.sending = false;
      },
      error: (err) => {
        this.actionError = 'No se pudo enviar: ' + (err.error?.message || 'Error de permisos o conexión.');
        this.sending = false;
      }
    });
  }

  /**
   * inc71: sube el archivo y lo deja preparado en el compositor.
   *
   * <p>La subida y el envío van separados a propósito: el agente ve el nombre y la vista
   * previa antes de mandarlo y puede descartarlo sin haber publicado nada en el chat.</p>
   */
  subirAdjunto(file: File) {
    if (!this.conversationId) return;
    this.uploadingAttachment = true;
    this.actionError = '';
    this.api.uploadAttachment(this.conversationId, file).subscribe({
      next: (res) => {
        this.uploadingAttachment = false;
        this.adjuntoListo = res;
      },
      error: (err) => {
        this.uploadingAttachment = false;
        this.actionError = err?.status === 400
          ? 'Formato de archivo no permitido. Se aceptan imágenes (JPG, PNG, GIF, WEBP) y documentos (PDF, Word, Excel, PowerPoint, TXT, CSV, ZIP).'
          : err?.status === 413
            ? 'El archivo supera el tamaño máximo permitido (10 MB).'
            : 'No se pudo subir el archivo. Inténtalo de nuevo.';
      }
    });
  }

  quitarAdjunto() {
    this.adjuntoListo = null;
  }

  /** Adjunto ya subido y pendiente de enviar. */
  adjuntoListo: AgentAttachmentUploadResponse | null = null;

  // ── Tomar (inc62) ──────────────────────────────────────────────────────────

  /**
   * "Tomar" no lleva cuerpo: el backend asigna la conversación al usuario autenticado.
   * Antes se enviaba `{agentId: 0}`, un campo que ningún DTO tiene, y la petición moría
   * con el mensaje técnico «userId: no debe ser nulo» pintado tal cual en pantalla.
   */
  assignToMe() {
    if (!this.conversationId || this.assigning) return;
    this.assigning = true;
    this.actionError = '';
    this.api.assignConversation(this.conversationId, {}).subscribe({
      next: (res) => {
        this.detail = res;
        this.assigning = false;
        this.toast.success({ title: 'Conversación tomada', message: 'Ya puedes responder al visitante.' });
      },
      error: (err) => {
        this.assigning = false;
        this.actionError = err?.status === 403
          ? 'No se pudo tomar la conversación porque ya está asignada a otro agente.'
          : err?.status === 400
            ? 'No se pudo tomar la conversación porque ya está cerrada.'
            : 'No se pudo tomar la conversación. Inténtalo nuevamente.';
      }
    });
  }

  // ── Transferir (inc67) ─────────────────────────────────────────────────────

  abrirTransferir() {
    this.showTransferModal = true;
    this.transferType = 'AGENT';
    this.selectedAgentId = null;
    this.selectedTeamId = null;
    this.transferNote = '';

    if (this.transferAgents.length === 0 || this.transferTeams.length === 0) {
      this.loadingTransferData = true;
      this.agentLookup.getAgents({ includePresence: true, includeCapacity: true }).subscribe({
        next: agents => { this.transferAgents = agents; this.loadingTransferData = false; },
        error: () => this.loadingTransferData = false
      });
      this.teamLookup.getTeams({ activeOnly: true }).subscribe({
        next: teams => { this.transferTeams = teams; },
        error: () => {}
      });
    }
  }

  cerrarTransferModal() {
    this.showTransferModal = false;
  }

  ejecutarTransferencia() {
    if (!this.conversationId || this.transfiriendo) return;
    if (this.transferType === 'AGENT' && !this.selectedAgentId) return;
    if (this.transferType === 'TEAM' && !this.selectedTeamId) return;

    // Los nombres son los del DTO del backend. Enviar `agentId`/`teamId` hacía que
    // `targetUserId` llegara vacío y la transferencia muriera siempre en validación.
    const payload = this.transferType === 'AGENT'
      ? { targetUserId: this.selectedAgentId!, note: this.transferNote.trim() || undefined }
      : { targetTeamId: this.selectedTeamId!, note: this.transferNote.trim() || undefined };

    this.transfiriendo = true;
    this.api.transferConversation(this.conversationId, payload).subscribe({
      next: (res) => {
        this.detail = res;
        this.actionError = '';
        this.transfiriendo = false;
        this.showTransferModal = false;
        this.toast.success({
          title: 'Conversación transferida',
          message: 'El nuevo responsable la verá en su bandeja con todo el historial.'
        });
      },
      error: (err) => {
        this.transfiriendo = false;
        this.actionError = err?.error?.message
          ? 'No se pudo transferir: ' + err.error.message
          : 'No se pudo transferir la conversación. Inténtalo nuevamente.';
      }
    });
  }

  // ── Finalizar (inc70) ──────────────────────────────────────────────────────

  abrirCerrar() {
    this.closeReason = '';
    this.showCloseModal = true;
  }

  cerrarCerrarModal() {
    this.showCloseModal = false;
  }

  confirmarCierre() {
    if (!this.conversationId || this.cerrando) return;
    this.cerrando = true;
    this.api.closeConversation(this.conversationId, { reason: this.closeReason.trim() || undefined }).subscribe({
      next: (res) => {
        this.detail = res;
        this.actionError = '';
        this.cerrando = false;
        this.showCloseModal = false;
        this.toast.success({
          title: 'Conversación finalizada',
          message: 'El motivo quedó registrado en el historial de la conversación.'
        });
      },
      error: (err) => {
        this.cerrando = false;
        this.actionError = err?.error?.message
          ? 'No se pudo finalizar: ' + err.error.message
          : 'No se pudo finalizar la conversación. Inténtalo nuevamente.';
      }
    });
  }

  // ── Ticket (inc68) ─────────────────────────────────────────────────────────

  abrirTicket() {
    if (!this.conversationId) return;
    this.ticketForm = this.emptyTicketForm();
    // Prefill con lo que ya se sabe de la conversación: es lo que pide el incidente
    // ("cuando sea posible, la información disponible debería completarse automáticamente").
    const visitante = this.detail?.contactSummary?.name || this.detail?.visitorName || 'visitante';
    this.ticketForm.subject = `Chat con ${visitante}`;
    this.ticketForm.description = this.transcripcionComoTexto();
    this.showTicketModal = true;
    this.cargarCatalogosTickets();
  }

  cerrarTicketModal() {
    this.showTicketModal = false;
  }

  private cargarCatalogosTickets() {
    if (this.ticketCatalogsLoaded) return;
    forkJoin({
      pipelines: this.ticketsConfigApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      statuses: this.ticketsConfigApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[]))),
      priorities: this.ticketsConfigApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[]))),
      categories: this.ticketsConfigApi.getCategories().pipe(catchError(() => of([] as TicketCategory[])))
    }).subscribe((res) => {
      // Solo catálogos vigentes: un estado desactivado en Tickets no debe poder elegirse aquí.
      this.ticketPipelines = res.pipelines.filter((p) => p.isActive !== false);
      this.ticketStatuses = res.statuses.filter((s) => s.isActive !== false);
      this.ticketPriorities = res.priorities.filter((p) => p.isActive !== false);
      this.ticketCategories = res.categories.filter((c) => c.isActive !== false);
      this.ticketCatalogsLoaded = true;
    });
  }

  crearTicket() {
    if (!this.conversationId || this.creatingTicket) return;
    const subject = this.ticketForm.subject.trim();
    if (!subject) return;

    this.creatingTicket = true;
    this.actionError = '';
    this.api.createTicketFromConversation(this.conversationId, {
      subject,
      description: this.ticketForm.description.trim() || undefined,
      pipelineId: this.ticketForm.pipelineId,
      statusId: this.ticketForm.statusId,
      priorityId: this.ticketForm.priorityId,
      categoryId: this.ticketForm.categoryId,
      contactId: this.detail?.contactId ?? null,
      companyId: this.detail?.companyId ?? null
    }).subscribe({
      next: (res) => {
        this.creatingTicket = false;
        this.showTicketModal = false;
        this.createdTicketId = res.id;
        this.createdTicketNumber = res.ticketNumber;
        this.toast.success({ title: 'Ticket creado', message: `Ticket ${res.ticketNumber} creado correctamente.` });
      },
      error: (err) => {
        this.creatingTicket = false;
        this.actionError = err?.status === 403 ? 'No tienes permiso para crear tickets.'
          : err?.error?.message ? 'No se pudo crear el ticket: ' + err.error.message
          : 'No se pudo crear el ticket. Inténtalo nuevamente.';
      }
    });
  }

  /** Abre el ticket vinculado en el módulo de Tickets (modal de detalle sobre la lista). */
  verTicket() {
    if (!this.createdTicketId) return;
    this.router.navigate(['/tickets'], { queryParams: { openTicket: this.createdTicketId } });
  }

  private emptyTicketForm(): TicketFormModel {
    return { subject: '', description: '', pipelineId: null, statusId: null, priorityId: null, categoryId: null };
  }

  private transcripcionComoTexto(): string {
    return this.transcript
      .filter((m) => (m.contentText || '').trim().length > 0)
      .map((m) => `[${this.etiquetaParticipante(m.participantType)}] ${m.contentText}`)
      .join('\n');
  }

  private etiquetaParticipante(tipo: AgentMessage['participantType']): string {
    switch (tipo) {
      case 'VISITOR': return 'Visitante';
      case 'AGENT': return 'Agente';
      case 'BOT': return 'Bot';
      default: return 'Sistema';
    }
  }

  // ── Transcript: exportar / privacidad ──────────────────────────────────────

  exportarTranscript(format: 'txt' | 'json') {
    if (!this.conversationId) return;
    if (!this.permissions.canExportTranscripts()) { this.actionError = this.permissions.getPermissionDeniedMessage('CHATFLOW_EXPORT_TRANSCRIPTS'); return; }
    this.api.exportTranscript(this.conversationId, format).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `transcript-${this.conversationId}.${format}`; a.click();
        URL.revokeObjectURL(url);
        this.toast.success({ title: 'Exportado', message: `Transcript descargado como .${format.toUpperCase()}.` });
      },
      error: (err) => {
        this.actionError = err?.status === 403 ? 'No tienes permiso para exportar transcripciones.'
          : err?.status === 404 ? 'El transcript ya fue eliminado o anonimizado.'
          : 'No se pudo exportar el transcript.';
      }
    });
  }

  solicitarAccionDestructiva(action: 'DELETE' | 'ANONYMIZE') {
    if (action === 'DELETE' && !this.permissions.canDeleteTranscripts()) {
      this.actionError = this.permissions.getPermissionDeniedMessage('CHATFLOW_DELETE_TRANSCRIPTS'); return;
    }
    if (action === 'ANONYMIZE' && !this.permissions.canManagePrivacy()) {
      this.actionError = this.permissions.getPermissionDeniedMessage('CHATFLOW_MANAGE_PRIVACY'); return;
    }
    this.showDestructiveConfirm = action;
  }

  cancelarDestructiva() { this.showDestructiveConfirm = null; }

  confirmarDestructiva() {
    if (!this.conversationId || !this.showDestructiveConfirm) return;
    const action = this.showDestructiveConfirm;
    this.showDestructiveConfirm = null;
    const obs = action === 'DELETE'
      ? this.api.deleteTranscript(this.conversationId)
      : this.api.anonymizeTranscript(this.conversationId);
    obs.subscribe({
      next: (res) => {
        if (action === 'DELETE') this.transcriptDeleted = true;
        else this.transcriptAnonymized = true;
        this.transcript = [];
        // Inc 7: el backend responde 204 sin cuerpo → `res` es null; leer `res.message`
        // lanzaba TypeError dentro del next y el toast de éxito nunca aparecía.
        this.toast.success({
          title: action === 'DELETE' ? 'Transcript eliminado' : 'Conversación anonimizada',
          message: res?.message || (action === 'DELETE'
            ? 'Los mensajes del transcript fueron eliminados.'
            : 'Los datos personales del visitante fueron reemplazados.')
        });
      },
      error: (err) => {
        this.actionError = err?.status === 403 ? 'No tienes permiso para esta acción.'
          : 'No se pudo completar la acción. Intenta de nuevo.';
      }
    });
  }

  /**
   * "Ver ficha del contacto" (Inc 6): abre la vista de DETALLE del contacto, no su edición.
   * `/contactos?openContact=<id>` es el deep-link que abre el modal de detalle (el mismo que
   * usa el buscador global), igual que `/tickets?openTicket=<id>` para los tickets.
   */
  verContacto() {
    if (this.detail?.contactId) {
      this.router.navigate(['/contactos'], { queryParams: { openContact: this.detail.contactId } });
    }
  }

  /**
   * Historial del contacto para el panel CRM.
   *
   * <p>Los tickets se piden al endpoint de historial del contacto, que devuelve un array con
   * los nombres de estado y prioridad ya resueltos. Antes se llamaba a `GET /api/tickets` con
   * filtros que ese endpoint no acepta y que además devuelve una página, no una lista: el
   * panel salía SIEMPRE vacío.</p>
   *
   * <p>Las conversaciones se filtran por `contactId` en el servidor. Antes ese filtro se
   * enviaba con un nombre que el backend ignoraba, así que el bloque "Conversaciones
   * anteriores" mostraba la bandeja entera como si todo fuera de este contacto.</p>
   */
  private loadHistory(contactId: number) {
    this.loadingHistory = true;
    this.ticketApi.getContactTicketHistory(contactId).subscribe({
      next: (tickets) => this.pastTickets = tickets,
      error: (err) => console.error(err)
    });

    this.api.getConversations({ contactId }).subscribe({
      next: (convs) => {
        this.pastConversations = convs.filter((c) => c.conversationId !== this.conversationId);
        this.loadingHistory = false;
      },
      error: (err) => {
        console.error(err);
        this.loadingHistory = false;
      }
    });
  }

  /** Estado de la conversación en el idioma de la interfaz, nunca el enum del backend. */
  estadoLegible(status?: string | null): string {
    return conversationStatusLabel(status);
  }

  /**
   * Texto del botón del ticket vinculado.
   *
   * Preferimos siempre el número público (TKT-1-100), que es como se busca el ticket en su
   * módulo. El "Ticket #id" solo aparece si el backend no pudo resolver el número, y aun así
   * el botón sigue llevando al ticket correcto.
   */
  get etiquetaTicket(): string {
    return this.createdTicketNumber || `Ticket #${this.createdTicketId}`;
  }

  /** Abre un ticket del historial en el módulo de Tickets. */
  verTicketDelHistorial(ticketId: number) {
    this.router.navigate(['/tickets'], { queryParams: { openTicket: ticketId } });
  }
}
